import type { Contact, Supplier } from 'features/suppliers/types';
import { getSuppliersData } from 'mocks/api/handlers/suppliersCache';

/**
 * Deterministic, additive seed for the "Update Supplier: edit-form validation & save" e2e epic
 * (DL-65996). Appends one dedicated row per story to org 2's (brand `page` fixture) cache, and one
 * deterministic company to org 123's cache (the fixed org the company-suggestion endpoint reads
 * from, `GET /api/meta/search/global-sub-suppliers`) — same convention as the sibling
 * `supplierEditEntryFixture.ts` (DL-65988). Distinct ids/sub-org-ids from every other epic's seeded
 * rows, including the sibling epic's `900101`-`900103`.
 *
 * Every mutating story gets its own dedicated row rather than sharing the sibling epic's
 * `SUPPLIER_EDIT_FULL_ID` fixture, so a PUT one test issues can never be observed by another test.
 *
 * Duplicated by value in `e2e/src/commons/suppliers/supplierEditValidation.helpers.ts` (e2e has no
 * path alias into `frontend/src` — established precedent, see the sibling fixture's header).
 */
const SUPPLIER_EDIT_ORG_ID = 2;
const SUGGESTION_ORG_ID = 123;

export const SUPPLIER_EDIT_VALIDATION_MANDATORY_ID = 'e2e-supplier-edit-validation-mandatory';
export const SUPPLIER_EDIT_VALIDATION_MANDATORY_SUB_ORG_ID = 900201;
export const SUPPLIER_EDIT_VALIDATION_MANDATORY_NAME = 'Cascade Fabrics Ltd';

export const SUPPLIER_EDIT_VALIDATION_SAVE_ID = 'e2e-supplier-edit-validation-save';
export const SUPPLIER_EDIT_VALIDATION_SAVE_SUB_ORG_ID = 900202;
export const SUPPLIER_EDIT_VALIDATION_SAVE_NAME = 'Harbor Knit Mills';

export const SUPPLIER_EDIT_VALIDATION_REPEAT_ID = 'e2e-supplier-edit-validation-repeat';
export const SUPPLIER_EDIT_VALIDATION_REPEAT_SUB_ORG_ID = 900203;
export const SUPPLIER_EDIT_VALIDATION_REPEAT_NAME = 'Ridgeline Trims Co';

export const SUPPLIER_EDIT_VALIDATION_OPTIONAL_ID = 'e2e-supplier-edit-validation-optional';
export const SUPPLIER_EDIT_VALIDATION_OPTIONAL_SUB_ORG_ID = 900204;
export const SUPPLIER_EDIT_VALIDATION_OPTIONAL_NAME = 'Willowmere Dyeing House';

export const SUPPLIER_EDIT_VALIDATION_BLANKNAME_ID = 'e2e-supplier-edit-validation-blankname';
export const SUPPLIER_EDIT_VALIDATION_BLANKNAME_SUB_ORG_ID = 900205;
export const SUPPLIER_EDIT_VALIDATION_BLANKNAME_NAME = 'Ironbridge Leatherworks';

export const SUPPLIER_EDIT_VALIDATION_WHITESPACE_ID = 'e2e-supplier-edit-validation-whitespace';
export const SUPPLIER_EDIT_VALIDATION_WHITESPACE_SUB_ORG_ID = 900206;
export const SUPPLIER_EDIT_VALIDATION_WHITESPACE_NAME = 'Marsh Hollow Textiles';

export const SUPPLIER_EDIT_VALIDATION_DIRTY_ID = 'e2e-supplier-edit-validation-dirty';
export const SUPPLIER_EDIT_VALIDATION_DIRTY_SUB_ORG_ID = 900207;
export const SUPPLIER_EDIT_VALIDATION_DIRTY_NAME = 'Copperfield Buttons Ltd';

export const SUPPLIER_EDIT_VALIDATION_DUPLICATE_ID = 'e2e-supplier-edit-validation-duplicate';
export const SUPPLIER_EDIT_VALIDATION_DUPLICATE_SUB_ORG_ID = 900208;
export const SUPPLIER_EDIT_VALIDATION_DUPLICATE_NAME = 'Thornfield Packaging Co';

export const SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ID = 'e2e-supplier-edit-validation-no-primary';
export const SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_SUB_ORG_ID = 900209;
export const SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_NAME = 'Estuary Yarn Works';
export const SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ADDITIONAL_CONTACT = {
  firstName: 'Petra',
  lastName: 'Lindqvist',
  email: 'petra.lindqvist@example.com',
  role: 'Logistics Coordinator',
} as const;

/** The known, deterministic company the company-suggestion endpoint (org 123's catalog) resolves,
 *  for the DL-65998 autofill story. Distinct id/name from org 123's default randomised catalog. */
export const SUPPLIER_SUGGESTION_ID = 'e2e-supplier-edit-validation-suggestion';
export const SUPPLIER_SUGGESTION_NAME = 'Aldergate Merino Supply';
export const SUPPLIER_SUGGESTION_VALUES = {
  address: '9 Aldergate Row',
  city: 'Gothenburg',
  zipCode: '41103',
  stateOrRegion: 'Vastra Gotaland',
  latitude: 57.7089,
  longitude: 11.9746,
  mid: 'MIDSUGGEST001',
  countryCode: 'SE',
} as const;

const baseContact = (seed: number, overrides: Partial<Contact> = {}): Contact => ({
  id: seed,
  firstName: 'Freya',
  lastName: 'Holt',
  email: `contact${seed}@example.com`,
  phone: '+4511223344',
  role: 'Sourcing Manager',
  isPrimaryContact: true,
  ...overrides,
});

const makeSupplier = (
  id: string,
  organizationId: number,
  name: string,
  overrides: Partial<Supplier> = {}
): Supplier => ({
  id,
  organizationId,
  name,
  relationType: 2,
  customId: `SUP-${organizationId}`,
  mid: `MID${organizationId}`,
  address: '1 Fixture Lane',
  zipCode: '4000',
  city: 'Odense',
  stateOrRegion: 'Funen',
  countryCode: 'DK',
  latitude: 55.4038,
  longitude: 10.4024,
  contacts: [baseContact(organizationId * 10 + 1)],
  facilities: [],
  archived: false,
  isArchived: false,
  isSupplierInUse: false,
  ...overrides,
});

const SUPPLIER_EDIT_VALIDATION_MANDATORY = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_MANDATORY_ID,
  SUPPLIER_EDIT_VALIDATION_MANDATORY_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_MANDATORY_NAME
);

const SUPPLIER_EDIT_VALIDATION_SAVE = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_SAVE_ID,
  SUPPLIER_EDIT_VALIDATION_SAVE_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_SAVE_NAME
);

const SUPPLIER_EDIT_VALIDATION_REPEAT = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_REPEAT_ID,
  SUPPLIER_EDIT_VALIDATION_REPEAT_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_REPEAT_NAME
);

/** Every optional field populated so the DL-66000 story has real values to clear. */
const SUPPLIER_EDIT_VALIDATION_OPTIONAL = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_OPTIONAL_ID,
  SUPPLIER_EDIT_VALIDATION_OPTIONAL_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_OPTIONAL_NAME,
  { customId: 'SUP-OPT-001', mid: 'MIDOPT001' }
);

const SUPPLIER_EDIT_VALIDATION_BLANKNAME = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_BLANKNAME_ID,
  SUPPLIER_EDIT_VALIDATION_BLANKNAME_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_BLANKNAME_NAME
);

/** A primary AND an additional contact, both with real data on every field, so a whitespace-only
 *  edit to one field still leaves a sibling field populated (DL-66002's drop-filter survival). */
const SUPPLIER_EDIT_VALIDATION_WHITESPACE = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_WHITESPACE_ID,
  SUPPLIER_EDIT_VALIDATION_WHITESPACE_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_WHITESPACE_NAME,
  {
    contacts: [
      baseContact(9020601, {
        firstName: 'Elin',
        lastName: 'Kristiansen',
        email: 'elin.kristiansen@example.com',
        role: 'Sourcing Manager',
        isPrimaryContact: true,
      }),
      baseContact(9020602, {
        firstName: 'Oskar',
        lastName: 'Bergstrom',
        email: 'oskar.bergstrom@example.com',
        role: 'Quality Lead',
        isPrimaryContact: false,
      }),
    ],
  }
);

const SUPPLIER_EDIT_VALIDATION_DIRTY = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_DIRTY_ID,
  SUPPLIER_EDIT_VALIDATION_DIRTY_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_DIRTY_NAME
);

const SUPPLIER_EDIT_VALIDATION_DUPLICATE = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_DUPLICATE_ID,
  SUPPLIER_EDIT_VALIDATION_DUPLICATE_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_DUPLICATE_NAME
);

/** No `isPrimaryContact: true` entry at all — only an additional contact (DL-66005). */
const SUPPLIER_EDIT_VALIDATION_NO_PRIMARY = makeSupplier(
  SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ID,
  SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_SUB_ORG_ID,
  SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_NAME,
  {
    contacts: [
      {
        id: 9020901,
        firstName: SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ADDITIONAL_CONTACT.firstName,
        lastName: SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ADDITIONAL_CONTACT.lastName,
        email: SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ADDITIONAL_CONTACT.email,
        phone: '+4599887766',
        role: SUPPLIER_EDIT_VALIDATION_NO_PRIMARY_ADDITIONAL_CONTACT.role,
        isPrimaryContact: false,
      },
    ],
  }
);

const SUPPLIER_SUGGESTION: Supplier = {
  id: SUPPLIER_SUGGESTION_ID,
  organizationId: SUGGESTION_ORG_ID,
  name: SUPPLIER_SUGGESTION_NAME,
  relationType: 2,
  customId: 'SUP-SUGGEST-001',
  mid: SUPPLIER_SUGGESTION_VALUES.mid,
  address: SUPPLIER_SUGGESTION_VALUES.address,
  zipCode: SUPPLIER_SUGGESTION_VALUES.zipCode,
  city: SUPPLIER_SUGGESTION_VALUES.city,
  stateOrRegion: SUPPLIER_SUGGESTION_VALUES.stateOrRegion,
  countryCode: SUPPLIER_SUGGESTION_VALUES.countryCode,
  latitude: SUPPLIER_SUGGESTION_VALUES.latitude,
  longitude: SUPPLIER_SUGGESTION_VALUES.longitude,
  contacts: [],
  facilities: [],
  archived: false,
  isArchived: false,
  isSupplierInUse: false,
};

let seeded = false;

/** Idempotent: appends the nine deterministic rows to org 2's cache, plus the suggestion company
 *  to org 123's cache. */
export const seedSupplierEditValidationFixture = (): void => {
  if (seeded) return;
  seeded = true;

  const data = getSuppliersData(SUPPLIER_EDIT_ORG_ID);
  data.push(
    SUPPLIER_EDIT_VALIDATION_MANDATORY,
    SUPPLIER_EDIT_VALIDATION_SAVE,
    SUPPLIER_EDIT_VALIDATION_REPEAT,
    SUPPLIER_EDIT_VALIDATION_OPTIONAL,
    SUPPLIER_EDIT_VALIDATION_BLANKNAME,
    SUPPLIER_EDIT_VALIDATION_WHITESPACE,
    SUPPLIER_EDIT_VALIDATION_DIRTY,
    SUPPLIER_EDIT_VALIDATION_DUPLICATE,
    SUPPLIER_EDIT_VALIDATION_NO_PRIMARY
  );

  const suggestionData = getSuppliersData(SUGGESTION_ORG_ID);
  suggestionData.push(SUPPLIER_SUGGESTION);
};
